// components/common/Typography/Typography.tsx
import { ReactNode, ReactElement, ElementType } from 'react';
import { Typography as MuiTypography, Box, TypographyProps as MuiTypographyProps, BoxProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

type GradientType = 'primary' | 'secondary' | 'warning' | 'danger' | 'success' | string;

export interface GradientTextProps extends Omit<MuiTypographyProps, 'variant'> {
  children: ReactNode;
  variant?: MuiTypographyProps['variant'];
  gradient?: GradientType;
  component?: ElementType;
  sx?: SxProps<Theme>;
}

/**
 * GradientText - Text with gradient background
 */
export function GradientText({
  children,
  variant = 'h4',
  gradient = 'primary',
  component,
  sx = {},
  ...props
}: GradientTextProps): ReactElement {
  const gradients: Record<string, string> = {
    primary: 'linear-gradient(90deg, #00FFD4, #00D4FF)',
    secondary: 'linear-gradient(90deg, #7B68EE, #00FFD4)',
    warning: 'linear-gradient(90deg, #FFE600, #FF6B35)',
    danger: 'linear-gradient(90deg, #FF3366, #FF6B35)',
    success: 'linear-gradient(90deg, #00FF88, #00FFD4)',
  };

  return (
    <MuiTypography
      variant={variant}
      component={component || 'span'}
      sx={{
        fontFamily: '"Rajdhani", sans-serif',
        fontWeight: 700,
        background: gradients[gradient] || gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiTypography>
  );
}

export interface MonoTextProps extends Omit<MuiTypographyProps, 'variant' | 'color'> {
  children: ReactNode;
  variant?: MuiTypographyProps['variant'];
  color?: string;
  component?: ElementType;
  sx?: SxProps<Theme>;
}

/**
 * MonoText - Monospace text for numbers/codes
 */
export function MonoText({
  children,
  variant = 'body1',
  color = '#00FFD4',
  component = 'span',
  sx = {},
  ...props
}: MonoTextProps): ReactElement {
  return (
    <MuiTypography
      variant={variant}
      component={component}
      sx={{
        fontFamily: '"Share Tech Mono", monospace',
        color,
        letterSpacing: '0.05em',
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiTypography>
  );
}

export interface PageTitleProps extends Omit<MuiTypographyProps, 'variant'> {
  children: ReactNode;
  subtitle?: string;
  gradient?: boolean;
  gradientColors?: GradientType;
  centered?: boolean;
  animate?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * PageTitle - Styled page title
 */
export function PageTitle({
  children,
  subtitle,
  gradient = false,
  gradientColors = 'primary',
  centered = false,
  animate = true,
  sx = {},
  ...props
}: PageTitleProps): ReactElement {
  return (
    <Box
      sx={{
        mb: 3,
        textAlign: centered ? 'center' : 'left',
        animation: animate ? 'fade-in 0.5s ease-out' : 'none',
        ...sx,
      }}
    >
      {gradient ? (
        <GradientText variant="h4" gradient={gradientColors} {...props}>
          {children}
        </GradientText>
      ) : (
        <MuiTypography
          variant="h4"
          sx={{
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 700,
            color: '#FFFFFF',
          }}
          {...props}
        >
          {children}
        </MuiTypography>
      )}
      {subtitle && (
        <MuiTypography
          variant="body2"
          sx={{
            color: '#8892A0',
            mt: 0.5,
            fontFamily: '"Exo 2", sans-serif',
          }}
        >
          {subtitle}
        </MuiTypography>
      )}
    </Box>
  );
}

export interface SectionTitleProps extends BoxProps {
  children: ReactNode;
  color?: string;
  icon?: ReactNode;
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * SectionTitle - Smaller section headers
 */
export function SectionTitle({
  children,
  color = '#00FFD4',
  icon,
  action,
  sx = {},
  ...props
}: SectionTitleProps): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        ...sx,
      }}
      {...props}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && (
          <Box sx={{ color, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <MuiTypography
          variant="h6"
          sx={{
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            color,
          }}
        >
          {children}
        </MuiTypography>
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
}

export interface LabelProps extends Omit<MuiTypographyProps, 'color'> {
  children: ReactNode;
  color?: string;
  uppercase?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Label - Small label text
 */
export function Label({
  children,
  color = '#8892A0',
  uppercase = true,
  sx = {},
  ...props
}: LabelProps): ReactElement {
  return (
    <MuiTypography
      variant="caption"
      sx={{
        color,
        fontFamily: '"Rajdhani", sans-serif',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: uppercase ? 'uppercase' : 'none',
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiTypography>
  );
}

export interface ErrorTextProps extends MuiTypographyProps {
  children: ReactNode;
  animate?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * ErrorText - Error message text
 */
export function ErrorText({ children, animate = true, sx = {}, ...props }: ErrorTextProps): ReactElement {
  return (
    <MuiTypography
      variant="body2"
      sx={{
        color: '#FF3366',
        backgroundColor: 'rgba(255, 51, 102, 0.1)',
        border: '1px solid #FF3366',
        borderRadius: '8px',
        p: 1.5,
        animation: animate ? 'shake 0.3s ease-in-out' : 'none',
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiTypography>
  );
}

export interface SuccessTextProps extends MuiTypographyProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * SuccessText - Success message text
 */
export function SuccessText({ children, sx = {}, ...props }: SuccessTextProps): ReactElement {
  return (
    <MuiTypography
      variant="body2"
      sx={{
        color: '#00FF88',
        backgroundColor: 'rgba(0, 255, 136, 0.1)',
        border: '1px solid #00FF88',
        borderRadius: '8px',
        p: 1.5,
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiTypography>
  );
}

export default GradientText;
